import { useState } from "react"
import { Button } from "@/components/ui/button"
import { useMessage, useThread, useThreadRuntime } from "@assistant-ui/react"
import { useChatPanel } from "../../hooks/use-chat-panel"
import type {
  ChoiceOption,
  UIComponentChoice,
} from "../../types/assistant.types"

interface ChoiceUIProps {
  data: UIComponentChoice
}

/** Normalize a mixed options array — accepts legacy flat strings as well
 *  as the current structured {label, value} objects. */
function normalizeOptions(
  opts: UIComponentChoice["options"],
): ChoiceOption[] {
  return opts.map((o) =>
    typeof o === "string" ? { label: o, value: o } : o,
  )
}

/** Concatenate the text parts of the first user message after `index`.
 *  Returns null when no follow-up user message exists yet. */
function findNextUserText(
  messages: ReadonlyArray<{
    role: string
    content: ReadonlyArray<{ type: string; text?: string }>
  }>,
  index: number,
): string | null {
  for (let i = index + 1; i < messages.length; i++) {
    const m = messages[i]
    if (m.role !== "user") continue
    const text = m.content
      .filter((p) => p.type === "text")
      .map((p) => p.text ?? "")
      .join("")
      .trim()
    return text || null
  }
  return null
}

export function ChoiceUI({ data }: ChoiceUIProps) {
  const threadRuntime = useThreadRuntime()
  const threadStatus = useChatPanel((s) => s.threadStatus)
  const messageIndex = useMessage((s) => s.index)
  const messages = useThread((s) => s.messages)
  const [selected, setSelected] = useState<string | null>(null)

  const options = normalizeOptions(data.options)

  // Two ways this card can be non-interactive:
  //   1. The whole conversation is closed (submitted / abandoned).
  //   2. This particular gate has already been answered earlier in the
  //      same draft. Without (2), reloading an in-progress draft would
  //      revive every prior choice card with empty local state and let
  //      the user click stale prompts that re-trigger the chat backend.
  // ``handleSelect`` appends the picked option's label verbatim, so the
  // user message that comes right after this turn (if any) is the
  // historical pick.
  const threadClosed = threadStatus != null && threadStatus !== "DRAFT"
  const historicalLabel = findNextUserText(messages, messageIndex)
  const isHistorical = threadClosed || historicalLabel !== null

  const handleSelect = (option: ChoiceOption) => {
    setSelected(option.value)
    // Send the human-readable label as the user's reply so the chat bubble
    // reads naturally. The backend resolves label → enum value by looking
    // up the most-recent choice block's options on the persisted thread.
    threadRuntime.append(option.label)
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {data.label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isSelected = isHistorical
            ? historicalLabel === option.label
            : selected === option.value
          return (
            <Button
              key={option.value}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              className={`h-8 text-xs transition-all ${
                isSelected
                  ? "shadow-sm"
                  : "bg-background hover:border-primary/40 hover:bg-primary/5"
              } ${isHistorical && !isSelected ? "opacity-60" : ""}`}
              disabled={selected !== null || isHistorical}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </Button>
          )
        })}
      </div>
    </div>
  )
}
